/** Wire contract shared by the browser, authoritative service and independent test clients. */
export const WORLD_W = 1200;
export const WORLD_H = 675;
export const PLAYER_W = 32;
export const PLAYER_H = 40;
export const TICK_RATE = 60;
export const SNAPSHOT_RATE = 20;
export const COLORS = ['#f3c969', '#80cbb0', '#eaa18c', '#94bce5', '#bdace2', '#e3b1cf', '#b4c975', '#f4a963'];
export const COLOR_NAMES = ['Honey', 'Fern', 'Coral', 'Rain', 'Lilac', 'Petal', 'Lime', 'Amber'];

export interface Rect { x: number; y: number; w: number; h: number }
export interface Point { x: number; y: number }
export interface Platform extends Rect {
  id: string;
  kind?: 'ground' | 'stone' | 'wood' | 'bridge';
  /** When defined, this platform is solid only while ALL named switches are active. */
  switchIds?: string[];
  motion?: { axis: 'x' | 'y'; distance: number; period: number; phase?: number };
}
export interface SwitchDef extends Rect {
  id: string;
  label: string;
  required: number;
  mode: 'hold' | 'latch';
  /** An optional number of seconds that the active state persists after releasing the plate. */
  grace?: number;
  /** Distribute connected helpers across this group after reserving scout/anchor roles.
   * Requirements shrink on disconnect; authored plate geometry stays unchanged. */
  teamWeight?: { group: string; reserve: number; priority: number };
  /** Effective requirement by CONNECTED count (index = connected couriers; the last
   * entry covers larger counts). Lets a level restate its own party rule after disconnects. */
  byConnected?: number[];
}
export interface GateDef extends Rect { id: string; switchIds: string[]; requireKey?: boolean }
export interface HazardDef extends Rect {
  id: string;
  kind: 'water' | 'bramble' | 'beam';
  /** Seconds on, seconds total, initial offset. Missing means always active. */
  cycle?: { on: number; period: number; phase: number };
  /** Before the team finds a key, only the protective switch can make this hazard safe. */
  cycleAfterKey?: boolean;
  disabledBy?: string[];
}
export interface KeyDef extends Rect { id: string }
export interface CrateDef extends Rect { id: string; weight?: number }
export interface SignDef extends Point { text: string; icon?: string }
export interface LevelDef {
  id: number;
  title: string;
  subtitle: string;
  mechanic: string;
  hint: string;
  theme: 'grove' | 'brook' | 'ruins' | 'night';
  par: number;
  spawn: Point[];
  platforms: Platform[];
  switches: SwitchDef[];
  gates: GateDef[];
  hazards: HazardDef[];
  keys: KeyDef[];
  crates: CrateDef[];
  exit: Rect;
  signs: SignDef[];
}
export interface PlayerInfo { id: string; name: string; color: number; connected: boolean; ready: boolean; host: boolean }
export interface InputState { left: boolean; right: boolean; jump: boolean; seq: number }
export interface PlayerState extends PlayerInfo, Rect {
  vx: number;
  vy: number;
  grounded: boolean;
  facing: number;
  entered: boolean;
  lastInput: number;
  supportId: string | null;
  coyote: number;
  jumpBuffer: number;
  jumpHeld: boolean;
}
export interface CrateState extends CrateDef { vx: number; vy: number; grounded: boolean }
export interface SwitchState { id: string; active: boolean; count: number; latched: boolean; graceLeft: number; required?: number }
export type GameEventType = 'jump' | 'land' | 'key' | 'switch' | 'enter' | 'reset' | 'complete' | 'start' | 'join' | 'leave' | 'emote';
export interface GameEvent { id: number; tick: number; type: GameEventType; playerId?: string; targetId?: string; x?: number; y?: number; text?: string }
export interface WorldState {
  tick: number;
  epoch: number;
  levelIndex: number;
  partySize: number;
  elapsed: number;
  deaths: number;
  complete: boolean;
  completedAt: number | null;
  players: PlayerState[];
  crates: CrateState[];
  switches: SwitchState[];
  collectedKeys: string[];
  events: GameEvent[];
  nextEventId: number;
}
export type RoomPhase = 'lobby' | 'playing' | 'cleared' | 'finished';
export interface RoomSnapshot {
  code: string;
  phase: RoomPhase;
  revision: number;
  serverTime: number;
  players: PlayerInfo[];
  level: LevelDef | null;
  world: WorldState | null;
  unlocked: number;
  countdown: number | null;
}
/** Browser-compatible ICE configuration, without a dependency on DOM types. */
export interface VoiceIceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}
/** One opted-in courier. `sessionId` is fresh per opt-in, so signalling for a previous
 * call can never reach its replacement. Mute state is server-authoritative. */
export interface VoiceParticipant {
  playerId: string;
  sessionId: string;
  muted: boolean;
}
export interface VoiceState {
  code: string;
  revision: number;
  enabled: boolean;
  participants: VoiceParticipant[];
}
/** The negotiation packets the service relays. It reconstructs only these fields;
 * a client's own object is never forwarded (see parseVoiceSignal in server/voice.ts). */
export type VoiceSignal =
  | { type: 'offer' | 'answer'; sdp: string }
  | { type: 'ice'; candidate: {
      candidate: string;
      sdpMid?: string | null;
      sdpMLineIndex?: number | null;
      usernameFragment?: string | null;
    } };
export interface VoiceSignalMessage {
  from: string;
  sessionId: string;
  targetSessionId: string;
  signal: VoiceSignal;
}

export type ClientMessage =
  | { type: 'create'; name: string; color?: number }
  | { type: 'join'; code: string; name: string; color?: number; token?: string }
  | { type: 'resume'; code: string; token: string }
  | { type: 'ready'; ready: boolean }
  | { type: 'start' }
  | { type: 'input'; input: InputState; epoch: number }
  | { type: 'retry'; epoch: number }
  | { type: 'next' }
  | { type: 'lobby' }
  | { type: 'select'; levelIndex: number }
  | { type: 'leave' }
  | { type: 'emote'; emote: 'hello' | 'here' | 'heart' }
  | { type: 'kick'; playerId: string }
  /** Ask for the current roster. The controller mounts after the first snapshot, so it
   * asks rather than relying on the push that went out with the welcome. */
  | { type: 'voice-sync' }
  | { type: 'voice-join'; sessionId: string }
  | { type: 'voice-leave'; sessionId: string }
  | { type: 'voice-mute'; sessionId: string; muted: boolean }
  | { type: 'voice-signal'; sessionId: string; to: string; targetSessionId: string; signal: VoiceSignal }
  | { type: 'ping'; time: number };
export type ServerMessage =
  | { type: 'welcome'; playerId: string; token: string; code: string }
  | { type: 'snapshot'; snapshot: RoomSnapshot }
  | { type: 'error'; code: string; message: string }
  /** Pushed to the whole room whenever the roster or a mute changes. */
  | { type: 'voice-state'; state: VoiceState }
  /** The answer to this courier's own 'voice-join': relay credentials are minted per player,
   * per join, so they are never part of the room-wide state. */
  | { type: 'voice-ready'; sessionId: string; iceServers: VoiceIceServer[] }
  | { type: 'voice-signal'; message: VoiceSignalMessage }
  | { type: 'pong'; time: number; serverTime: number };

export const EMPTY_INPUT: InputState = { left: false, right: false, jump: false, seq: 0 };
